import React, { useMemo } from 'react';
import { X } from '@piiqtechnologies/svg-icons';
import { PeekPopover, PeekBox, PeekTypography, PeekIconButton, PeekSkeleton, PeekButton, PeekDivider } from '@piiqtechnologies/ui-components';
import { NOTIFICATIONS_FEED_PAGE_SIZE } from '../../../../services/notificationsService.js';
import { groupNotificationsByRelease } from '../../dates.js';
import { isShowableItem } from '../../dialogs/catalog.js';
import { NotificationEntry } from '../notification-entry/NotificationEntry.js';

// The What's New list (appendix §5.5, PRD FR-5), as the product design draws it (Figma "What's new
// drawer"): a card that opens right beside the What's New entry, over the page without dimming it.
// It holds the most recent announcements aimed at this customer, grouped by month, 15 at a time,
// scrolling inside the card under a fixed header. Its X, Esc or a click outside closes it, and focus
// goes back to the entry. It never hints at announcements aimed at someone else: an empty list is just
// "up to date".
const WIDTH = 400;
const MAX_HEIGHT = 556;
/** Where the design puts the card: 17px right of the entry, 4px above its top. */
const GAP_FROM_ENTRY = 17;
const ABOVE_ENTRY_TOP = 4;
/** With no entry to open beside (a custom trigger), it opens at the top left of the page. */
const FALLBACK_POSITION = { top: 88, left: 16 };
/**
 * The card's top left corner, beside the entry as the design places it. Handed to the popover as a
 * position (not margins on the card) so that it keeps the card inside the window from there.
 */
const positionBeside = (anchorEl) => {
    if (!anchorEl)
        return FALLBACK_POSITION;
    const entry = anchorEl.getBoundingClientRect();
    return { top: entry.top - ABOVE_ENTRY_TOP, left: entry.right + GAP_FROM_ENTRY };
};
const HEADING_ID = "whats-new-drawer-heading";
/** Figma "Shadow-cards". */
const SHADOW = "0px 2px 23px 3px rgba(227, 227, 227, 0.55)";
/** The design's slim scrollbar (indigo/20). */
const SCROLLBAR = {
    scrollbarWidth: "thin",
    scrollbarColor: "#d2d2d7 transparent",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#d2d2d7", borderRadius: 20 },
};
const Divider = () => React.createElement(PeekDivider, { sx: { borderColor: "grey.200" } });
const Message = ({ children, testId }) => (React.createElement(PeekTypography, { variant: "h4", "data-testid": testId, sx: { color: "grey.400", textAlign: "center", px: "24px", py: 3 } }, children));
const NotificationDrawer = ({ open, anchorEl, items, hasMore, isLoading, hasError = false, onClose, onEntryClick, onEntryOpen, onLoadMore, onRetry, now, }) => {
    // One this build can't draw, or with no words written, has nothing to list.
    const listed = items.filter(isShowableItem);
    const groups = groupNotificationsByRelease(listed, now);
    const isFirstLoad = isLoading && !items.length;
    // Measured as it opens: the sidebar the entry sits in doesn't scroll with the page.
    const anchorPosition = useMemo(() => (open ? positionBeside(anchorEl) : FALLBACK_POSITION), [open, anchorEl]);
    return (React.createElement(PeekPopover, { open: open, onClose: onClose, anchorReference: "anchorPosition", anchorPosition: anchorPosition, transformOrigin: { vertical: "top", horizontal: "left" }, PaperProps: {
            "data-testid": "whats-new-drawer",
            role: "dialog",
            "aria-labelledby": HEADING_ID,
            sx: {
                width: WIDTH,
                maxWidth: "calc(100vw - 32px)",
                maxHeight: `min(${MAX_HEIGHT}px, calc(100vh - 32px))`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: "8px",
                boxShadow: SHADOW,
            },
        } },
        React.createElement(PeekBox, { sx: { display: "flex", alignItems: "flex-start", gap: "16px", px: "24px", pt: "24px", pb: "20px" } },
            React.createElement(PeekBox, { sx: { flex: 1, display: "flex", flexDirection: "column", gap: "6px" } },
                React.createElement(PeekTypography, { id: HEADING_ID, variant: "h2", sx: { color: "text.primary", fontSize: 20, lineHeight: "28px", fontWeight: 300 } }, "What's New!")),
            React.createElement(PeekIconButton, { bordered: false, "aria-label": "Close What's New", onClick: onClose, sx: { color: "grey.400" } },
                React.createElement(X, { width: 16, height: 16 }))),
        React.createElement(Divider, null),
        React.createElement(PeekBox, { sx: Object.assign({ flex: 1, minHeight: 0, overflowY: "auto" }, SCROLLBAR), "data-testid": "whats-new-drawer-list" },
            isFirstLoad && (React.createElement(PeekBox, { "aria-label": "Loading announcements", sx: { px: "24px", py: "20px" } }, [0, 1, 2].map((row) => (React.createElement(PeekSkeleton, { key: row, variant: "rectangular", height: 110, sx: { mb: 1.6, borderRadius: "8px" } }))))),
            !isLoading && !hasError && !listed.length && !hasMore && (React.createElement(Message, { testId: "whats-new-drawer-empty" }, "Nothing to see here right now \u2014 you're up to date.")),
            groups.map((group) => (React.createElement(PeekBox, { component: "section", key: group.label, "aria-label": group.label },
                React.createElement(PeekTypography, { variant: "body1", sx: {
                        color: "grey.400",
                        fontWeight: "fontWeightMedium",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        px: "24px",
                        pt: "16px",
                    } }, group.label),
                group.items.map((item) => (React.createElement(React.Fragment, { key: item.id },
                    React.createElement(NotificationEntry, { item: item, onClick: onEntryClick, onOpen: onEntryOpen }),
                    React.createElement(Divider, null))))))),
            hasError && (React.createElement(PeekBox, { role: "alert", "data-testid": "whats-new-drawer-error", sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1.2, p: "24px" } },
                React.createElement(PeekTypography, { variant: "h4", sx: { color: "grey.400", textAlign: "center" } }, items.length ? "Couldn't load older announcements." : "Couldn't load What's new."),
                onRetry && (React.createElement(PeekButton, { variant: "outlined", color: "info", size: "large", onClick: onRetry }, "Try again")))),
            items.length > 0 && hasMore && !hasError && (React.createElement(PeekBox, { sx: { display: "flex", justifyContent: "center", p: "20px" } },
                React.createElement(PeekButton, { variant: "outlined", color: "info", size: "large", disabled: isLoading, onClick: onLoadMore }, isLoading ? "Loading…" : `View ${NOTIFICATIONS_FEED_PAGE_SIZE} older items`))),
            listed.length > 0 && !hasMore && !hasError && (React.createElement(Message, { testId: "whats-new-drawer-end" }, "You're all caught up. Nothing older.")))));
};

export { NotificationDrawer, NotificationDrawer as default };
